import { action, observable } from "mobx";
import { BaasicDropdownStore, BaseEditViewStore } from 'core/stores';
import { NikoFacilityOrderReasonForChangeModalForm } from 'application/niko-facility-order/forms';
import { EligibilityStatusService } from 'application/eligibility-status/services';
import { NikoPatientDetailService, NikoPatientTemplateService } from "application/niko-patient/services";
import _ from 'lodash';
import { ModalParams } from 'core/models';

class NikoFacilityOrderReasonForChangeModalStore extends BaseEditViewStore {
    @observable accessor patientTemplates = [];
    @observable accessor showTemplates = false;
    @observable accessor nikoPatientDetail;
    @observable accessor isEligibleInsuranceStatus = false;
    @observable accessor selectedTabItemAbrv = 'insurance-eligibility';
    tabItems = [{ abrv: 'insurance-eligibility', name: 'Insurance Eligibility' }, { abrv: 'insurance-plans', name: 'Insurance Plans' }];

    constructor(rootStore, componentProps) {
        const nikoPatientDetailService = new NikoPatientDetailService(rootStore.application.baasic.apiClient);
        const nikoPatientDetail = componentProps.modalParams.data.nikoPatientDetail;
        super(rootStore, {
            name: 'niko-facility-order-reason-for-change-modal-store',
            Form: NikoFacilityOrderReasonForChangeModalForm,
            id: nikoPatientDetail.id,
            authorization: 'portalSection.update',
            actions: () => {
                return {
                    get: async () => {
                        await this.getPatientTemplates(nikoPatientDetail);
                        return nikoPatientDetail;
                    },
                    update: async (resource) => {
                        const response = await nikoPatientDetailService.updateNikoPatientReasonForChange(resource);
                        return response.data;
                    },
                }
            },
            FormClass: NikoFacilityOrderReasonForChangeModalForm,
            autoInit: false
        });

        this.rootStore = rootStore;
        this.nikoPatientDetailService = nikoPatientDetailService;
        this.modalParams = componentProps.modalParams;
        this.nikoPatientDetail = componentProps.modalParams.data.nikoPatientDetail;
        this.getNikoPatient = this.modalParams.data.getNikoPatient;
        this.showTemplateChangeWarning = this.modalParams.data.showTemplateChangeWarning;
        this.pageName = this.modalParams.data.pageName;
        this.sendOrder = this.modalParams.data.sendOrder;

        // ISG-2621: pop-ups shown on the first-tab (Insurance Eligibility) Save.
        // E1 - orange warning, confirm before applying an eligibility-status change.
        this.eligibilityWarningModal = new ModalParams({});
        this.eligibilityWarningModal.yesLabel = 'MODAL.CONFIRM_LABEL';
        this.eligibilityWarningModal.noLabel = 'MODAL.CANCEL_LABEL';
        this.eligibilityWarningModal.swapButtons = true;
        this.eligibilityWarningModal.compactMessage = true;
        // E2 - red error, blocks the change when a covered PSO was already billed to insurance.
        this.eligibilityErrorModal = new ModalParams({});
        this.eligibilityErrorModal.iconClassName = 'modal-error';
        this.eligibilityErrorModal.yesLabel = 'MODAL.CLOSE';
        this.eligibilityErrorModal.doNotShowCancel = true;
        this.eligibilityErrorModal.compactMessage = true;
        this.eligibilityErrorModal.onConfirm = () => this.eligibilityErrorModal.close();
        // E3 - orange acknowledge (no X), shown when a PAR flag was changed.
        this.parAcknowledgeModal = new ModalParams({});
        this.parAcknowledgeModal.yesLabel = 'MODAL.ACKNOWLEDGE';
        this.parAcknowledgeModal.doNotShowCancel = true;
        this.parAcknowledgeModal.compactMessage = true;

        this.eligibilityStatusService = new EligibilityStatusService(rootStore.application.baasic.apiClient);
        this.isEligibleInsuranceStatus = this.nikoPatientDetail.eligibilityStatus && this.nikoPatientDetail.eligibilityStatus.name === "Eligible";
        this.isInitialEligibleInsuranceStatus = this.isEligibleInsuranceStatus;

        this.eligibilityStatusStore = new BaasicDropdownStore({
            filterable: true,
        }, {
            fetchFunc: async (term) => {
                const response = await this.eligibilityStatusService.find({
                    searchQuery: term,
                    rpp: 50
                });
                return response.data.item;
            },
            onChange: async () => {
                this.isEligibleInsuranceStatus = this.eligibilityStatusStore.value && this.eligibilityStatusStore.value.name === "Eligible";
            }
        });

        if (this.nikoPatientDetail.eligibilityStatus) {
            this.eligibilityStatusStore.setValue({
                id: this.nikoPatientDetail.eligibilityStatusId,
                name: this.nikoPatientDetail.eligibilityStatus.name
            });
        }

        this.fetch([
            this.initialize()
        ]);
    }

    async initialize() {
        if (this.isEdit) {
            await this.getResource(this.id);
        } else {
            this.form.clear();
        }
    }

    async getPatientTemplates(nikoPatientDetail) {
        if (nikoPatientDetail.patientTemplates) {
            this.patientTemplates = _.map(nikoPatientDetail.patientTemplates, i => { return { ...i, itemsVisible: false }; })
        }
        else {
            const patientTemplateService = new NikoPatientTemplateService(this.rootStore.application.baasic.apiClient);
            const response = await patientTemplateService.find({
                nikoPatientDetailId: nikoPatientDetail.id,
                isDiscontinued: false,
                embed: "SubCategory,PatientTemplateItems",
                fields: "id,isPriorAuthNeeded,subCategory,patientTemplateItems",
                orderBy: "DateCreated",
                pageNumber: 1,
                pageSize: 10000
            });
            this.patientTemplates = _.map(response.data.item, i => { return { ...i, itemsVisible: false }; });
        }
    }

    @action.bound
    async setTabItem(tabItemAbrv) {
        this.selectedTabItemAbrv = tabItemAbrv;
    }

    // ISG-2625: a Niko Sales Order exists for this patient/DOS once any of the patient's templates has a
    // Niko order id. After that, insurance-policy edits must be made in Niko, so the second ("Insurance
    // Plans") tab of the edit-insurances popup is disabled. Before it exists (Day 1 / DNS), both tabs stay open.
    // This restriction applies on the PSO page only - the shared edit-insurances modal is also used by the Niko
    // facility-order pipeline pages (US Intake, Insurance Review, Clinical Review) and Patient Management, where
    // the second tab must stay accessible.
    get isInsurancePlansTabDisabled() {
        if (this.pageName !== 'pso') return false;
        const templates = this.nikoPatientDetail && this.nikoPatientDetail.patientTemplates;
        return !!(templates && templates.some(t => t.nikoOrderId));
    }

    @action.bound
    changeTemplatesVisibility() {
        this.showTemplates = !this.showTemplates;
    }

    @action.bound
    changeItemsVisibility(patientTemplate) {
        patientTemplate.itemsVisible = !patientTemplate.itemsVisible;
    }

    @action.bound
    setPriorAuthNeededd(patientTemplate) {
        patientTemplate.isPriorAuthNeeded = !patientTemplate.isPriorAuthNeeded;
    }

    @action.bound
    resetPARFlags() {
        this.patientTemplates.forEach(t => {
            t.isPriorAuthNeeded = false;
        });
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            const patientTemplates = _.map(this.patientTemplates, t => { return { id: t.id, isPriorAuthNeeded: t.isPriorAuthNeeded }; });
            const payload = { ...resource, id: this.nikoPatientDetail.id, patientTemplates: patientTemplates, pageName: this.pageName };

            // ISG-2719: the ISG-2381 edit-insurances gating (E1 warning / E2 error / E3 PAR acknowledge) applies on the
            // PSO page only. This modal is shared by the other niko-facility-order pipeline pages (Clinical Review,
            // Insurance Review, US/Initial Intake) and Patient Management, where the Save must keep the old behavior:
            // skip the precheck and apply the change directly with no pop-up.
            if (this.pageName !== 'pso') {
                await this.applyReasonForChange(payload, false);
                return;
            }

            // ISG-2621: ask the backend which pop-up (if any) applies before saving.
            const precheckResponse = await this.nikoPatientDetailService.precheckNikoPatientReasonForChange(payload);
            const customValues = (precheckResponse && precheckResponse.data && precheckResponse.data.customValues) || {};
            const scenario = customValues.scenario;
            const parUpdated = customValues.parUpdated === true || customValues.parUpdated === 'true';

            // E2: a covered PSO was already billed to insurance - block the change.
            if (scenario === 'Blocked') {
                this.form.setFieldsDisabled(false);
                this.eligibilityErrorModal.open({
                    question: "The patient's order(s) was billed to insurance.",
                    message: "To change the eligibility status (and bill the facility), please contact AR."
                });
                return;
            }

            // E1: eligibility status changed - confirm before applying. Keep the confirm modal open (showing its
            // loading spinner) until the save fully completes, then close it together with the edit modal so the
            // user never sees the edit-insurances modal lingering on its own.
            if (scenario === 'ConfirmationRequired') {
                this.eligibilityWarningModal.onConfirm = async () => {
                    this.eligibilityWarningModal.setLoading(true);
                    try {
                        await this.applyReasonForChange(payload, parUpdated);
                    }
                    finally {
                        this.eligibilityWarningModal.setLoading(false);
                        this.eligibilityWarningModal.close();
                    }
                };
                this.eligibilityWarningModal.onCancel = () => {
                    this.eligibilityWarningModal.close();
                    this.form.setFieldsDisabled(false);
                };
                this.eligibilityWarningModal.open({
                    question: "Eligibility Status will be updated for reporting purposes.",
                    message: "Please confirm to apply the change."
                });
                return;
            }

            // No eligibility change (note / effective date / PAR-only edits) - apply directly.
            await this.applyReasonForChange(payload, parUpdated);
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    // ISG-2621: persists the reason-for-change and, when a PAR flag changed (E3), shows the "clinical statuses were
    // not changed automatically" acknowledge pop-up before closing the edit modal.
    @action.bound
    async applyReasonForChange(payload, parUpdated) {
        try {
            await this.actions.update(payload);
            await this.getNikoPatient();

            if (parUpdated) {
                this.parAcknowledgeModal.onConfirm = async () => {
                    this.parAcknowledgeModal.close();
                    await this.modalParams.close();
                    setTimeout(() => this.notifySuccessUpdate(this.name), 10);
                };
                this.parAcknowledgeModal.open({
                    question: "PAR was updated.",
                    message: "Clinical status(es) were not changed automatically; please update the clinical statuses accordingly.",
                    hideClose: true
                });
                return;
            }

            await this.modalParams.close();
            setTimeout(() => this.notifySuccessUpdate(this.name), 10);
        }
        catch (err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    // @action.bound
    // async openRelationshipChangeModal() {
    //     this.relationshipChangeModal.open({ patientKey: this.patient.patientKey });
    // }
}

export default NikoFacilityOrderReasonForChangeModalStore;
